import { useEffect, useState } from "react";
import { II } from "../../components/common/Icons";
import PMModal from "./PMModal";
import { usePMData } from "../../context/PMContext";
import api from "../../services/api";
import { formatDateOnly } from "../../utils/helpers";
import "../../assets/styles/PM/PMReportsPg.css";

function PMReportsPg() {
  const { pmHrReports, pmProjects, handleReportAction, loading, refreshPMData } = usePMData();

  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [actionComment, setActionComment] = useState("");
  const [fineAmount, setFineAmount] = useState("");
  const [resendComment, setResendComment] = useState("");
  const [logs, setLogs] = useState([]);
  const [success, setSuccess] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [viewer, setViewer] = useState({ open: false, url: "", name: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setReports(Array.isArray(pmHrReports) ? pmHrReports : []);
  }, [pmHrReports]);

  const pending = reports.filter(r => String(r.status || "").toLowerCase() === "pending").length;
  const filtered = filter === "all" ? reports
    : filter === "pending" ? reports.filter(r => String(r.status || "").toLowerCase() === "pending")
    : reports.filter(r => r.status === filter);

  const openDetail = async (report) => {
    setSelected(report);
    setActionComment("");
    setShowDetail(true);
    try {
      const rows = await api.getPMReportLogs(report.rid);
      setLogs(Array.isArray(rows) ? rows : []);
    } catch { setLogs([]); }
  };

  const doAction = async (status) => {
    if (!selected?.rid || submitting) return;
    if (isLostAsset(selected) && !actionComment.trim()) {
      setErrorMsg("Please add a comment before submitting."); setTimeout(() => setErrorMsg(""), 2600); return;
    }
    setSubmitting(true);
    try {
      if (isLostAsset(selected)) {
        // Lost asset: use dedicated fine API
        await api.pmSubmitLostFine({
          reportId: selected.rid,
          comment: actionComment,
          fineAmount: fineAmount || 0
        });
        setShowDetail(false); setSelected(null); setFineAmount(""); setActionComment("");
        setSuccess("✅ Fine set & report sent to HR!");
      } else {
        await handleReportAction(selected.rid, status, actionComment || `${status} by PM`);
        setShowDetail(false); setSelected(null);
        setSuccess(status === "Approved" ? "✅ Report submitted to HR!" : `Report ${status}`);
      }
      await refreshPMData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setErrorMsg(`Action failed: ${e.message}`);
      setTimeout(() => setErrorMsg(""), 2600);
    }
    setSubmitting(false);
  };

  const doResend = async () => {
    if (!selected?.rid || !resendComment.trim()) return;
    try {
      await handleReportAction(selected.rid, "resend", resendComment);
      setShowResend(false); setSelected(null); setResendComment("");
      setSuccess("Resend request sent to HR");
      await refreshPMData();
      setTimeout(() => setSuccess(""), 2500);
    } catch (e) {
      setErrorMsg(`Resend failed: ${e.message}`);
      setTimeout(() => setErrorMsg(""), 2600);
    }
  };

  // Download report content as text
  const downloadReportText = (report) => {
    const content = report.hrComment || report.hr_comment || report.title || "No content";
    const blob = new Blob([content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${report.rid || "report"}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const isLostAsset = (r) => String(r.type || "").toLowerCase().includes("lost");

  if (loading) return <div className="pmreportspg-inline-1">Loading reports...</div>;

  return (
    <div>
      <div className="sec">HR Reports</div>
      {success && <div className="toast">{success}</div>}
      {errorMsg && <div className="toast pmreportspg-inline-2">{errorMsg}</div>}

      {/* Filters */}
      <div className="pmreportspg-inline-3">
        {["all","pending","Approved","Rejected","Reviewed"].map(k =>
          <button key={k} className={`${filter===k?"btn":"btn-o"} pmreportspg-dyn-1`} onClick={() => setFilter(k)}>
            {k} ({k==="all"?reports.length:k==="pending"?pending:reports.filter(r=>r.status===k).length})
          </button>
        )}
      </div>

      <div className="cd">
        <div className="cd-h">
          <div className="cd-t">Reports From HR</div>
          <div className="cd-s">{filtered.length} shown · {pending} pending</div>
        </div>
        <div className="cd-b pmreportspg-inline-5">
          <table className="tbl">
            <thead>
              <tr>
                <th>ID</th><th>Title</th><th>Type</th><th>Project</th>
                <th>Status</th><th>File / Report</th><th>PM Note</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan="8" className="pmreportspg-inline-6">No reports</td></tr>
                : filtered.map(r => (
                <tr key={r.rid} style={ isLostAsset(r) ? { background:"#fff8f8" } : {} }>
                  <td className="pmreportspg-inline-7">
                    {r.rid}
                    {isLostAsset(r) && <span style={{ marginLeft:4, fontSize:10, background:"#dc2626", color:"#fff", borderRadius:4, padding:"1px 5px" }}>LOST</span>}
                  </td>
                  <td className="pmreportspg-inline-8">{r.title}</td>
                  <td>{r.type}</td>
                  <td>{pmProjects.find(p=>p.pid===r.project)?.name||r.project_name||r.project||"—"}</td>
                  <td>
                    <span className={"chip "+(r.status==="Approved"?"chip-ok":r.status==="Rejected"?"chip-dn":"chip-wn")}>
                      {r.status}
                    </span>
                  </td>
                  <td>
                    {isLostAsset(r)
                      ? <button className="btn-o btn-s" onClick={()=>downloadReportText(r)}>
                          {II("download",11)} Download
                        </button>
                      : (r.fileUrl||r.file_url)
                        ? <div className="pmreportspg-inline-9">
                            <button className="btn-o btn-s" onClick={()=>setViewer({open:true,url:api.resolveFileUrl(r.fileUrl||r.file_url),name:r.fileName||r.file_name||"report"})}>
                              {II("eye",11)} Preview
                            </button>
                            <button className="btn-o btn-s" onClick={()=>api.downloadFile(r.fileUrl||r.file_url,r.fileName||r.file_name||"report")}>
                              {II("download",11)} Download
                            </button>
                          </div>
                        : "—"
                    }
                  </td>
                  <td className="pmreportspg-inline-10">{r.pmNote||"—"}</td>
                  <td>
                    <button className="btn-o btn-s" onClick={()=>openDetail(r)}>{II("eye",11)} Review</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detail Modal ── */}
      <PMModal open={showDetail} onClose={()=>setShowDetail(false)} title={`Report: ${selected?.title||""}`} wide>
        {selected && (
          <div>
            <div className="pmreportspg-inline-11">
              <b>ID:</b> {selected.rid} | <b>Status:</b> {selected.status}
              {isLostAsset(selected) && <span style={{marginLeft:8,background:"#fef2f2",color:"#dc2626",borderRadius:4,padding:"2px 8px",fontSize:11,fontWeight:700}}>🔴 Lost Asset Report</span>}
            </div>

            {/* Lost Asset: show full report content */}
            {isLostAsset(selected) && (selected.hrComment||selected.hr_comment) && (
              <div style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,padding:"10px 14px",marginBottom:12,maxHeight:280,overflowY:"auto"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#64748b",marginBottom:6,textTransform:"uppercase"}}>📋 Report Content</div>
                <pre style={{fontSize:12,color:"#374151",whiteSpace:"pre-wrap",margin:0,fontFamily:"monospace",lineHeight:1.6}}>
                  {selected.hrComment||selected.hr_comment}
                </pre>
              </div>
            )}

            {/* Non-lost: show file */}
            {!isLostAsset(selected) && selected.hrComment && (
              <div className="pmreportspg-inline-12">
                <div className="pmreportspg-inline-13">HR Comment</div>
                <div className="pmreportspg-inline-14">{selected.hrComment}</div>
              </div>
            )}

            {/* Download button for lost asset */}
            {isLostAsset(selected) && (
              <button onClick={()=>downloadReportText(selected)}
                style={{width:"100%",padding:"7px",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,fontSize:12,color:"#4f46e5",fontWeight:600,cursor:"pointer",marginBottom:12}}>
                📥 Download Report (.txt)
              </button>
            )}

            {/* Fine Amount for lost asset */}
            {isLostAsset(selected) && (
              <div style={{marginBottom:10}}>
                <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>
                  💰 Fine Amount (₹)
                </label>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <input
                    type="number" min="0" step="0.01"
                    value={fineAmount}
                    onChange={e=>setFineAmount(e.target.value)}
                    placeholder="Enter fine amount (0 = no fine)"
                    style={{flex:1,padding:"8px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none"}}
                  />
                  <span style={{fontSize:12,color:"#64748b",whiteSpace:"nowrap"}}>Leave 0 for no fine</span>
                </div>
              </div>
            )}

            {/* PM Comment box */}
            <textarea className="fta" rows={3} value={actionComment}
              onChange={e=>setActionComment(e.target.value)}
              placeholder={isLostAsset(selected)?"Add your review comment (required)...":"PM comment"}
            />

            {/* Actions */}
            <div className="pmreportspg-inline-15">
              {isLostAsset(selected) ? (
                <>
                  <button className="btn btn-ok" onClick={()=>doAction("Approved")} disabled={submitting}>
                    {submitting?"Submitting...":"📤 Set Fine & Send to HR"}
                  </button>
                  <button className="btn btn-dn" onClick={()=>doAction("Rejected")} disabled={submitting}>
                    {II("x",13)} Reject
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-ok" onClick={()=>doAction("Approved")} disabled={submitting}>{II("chk",13)} Approve</button>
                  <button className="btn btn-dn" onClick={()=>doAction("Rejected")} disabled={submitting}>{II("x",13)} Reject</button>
                  <button className="btn pmreportspg-inline-16" onClick={()=>{setShowDetail(false);setShowResend(true);}}>
                    {II("send",13)} Resend
                  </button>
                </>
              )}
            </div>

            {/* Logs */}
            <div className="pmreportspg-inline-17">
              <div className="pmreportspg-inline-18">Timeline</div>
              {logs.length===0
                ? <div className="pmreportspg-inline-19">No logs</div>
                : logs.map(l =>
                  <div key={l.id} className="pmreportspg-inline-20">
                    <div className="pmreportspg-inline-21">{l.action}</div>
                    <div className="pmreportspg-inline-22">{l.note||"—"}</div>
                    <div className="pmreportspg-inline-23">{formatDateOnly(l.created_at)}</div>
                  </div>
                )
              }
            </div>
          </div>
        )}
      </PMModal>

      <PMModal open={showResend} onClose={()=>setShowResend(false)} title="Request Resend">
        <textarea className="fta" rows={4} value={resendComment} onChange={e=>setResendComment(e.target.value)} placeholder="Reason for resend..." />
        <div className="pmreportspg-inline-24">
          <button className="btn btn-dn" onClick={doResend} disabled={!resendComment.trim()}>{II("send",13)} Send Resend</button>
        </div>
      </PMModal>

      {viewer.open && (
        <div className="pmreportspg-inline-25">
          <div className="pmreportspg-inline-26">
            <div className="pmreportspg-inline-27">
              <div className="pmreportspg-inline-28">{viewer.name}</div>
              <button className="btn-o btn-s" onClick={()=>setViewer({open:false,url:"",name:""})}>Close</button>
            </div>
            <iframe title="pm-report-viewer" src={viewer.url} className="pmreportspg-inline-29" />
          </div>
        </div>
      )}
    </div>
  );
}

export default PMReportsPg;
